// [2026-11-02] 타임존은 테넌트 설정(config/tenant.js) 기준 — admin-frontend/src/utils/dateTime.js 와 동일.
// DSK/DSK2027 = Asia/Seoul(KST), AP = Asia/Hong_Kong(HKT). (이전 HKT 고정 → DSK 미팅 시간이 1시간 이르게 표시)
import { getTimezone, getTimezoneLabel } from '../config/tenant';

export const APP_TIMEZONE = getTimezone();
export const APP_TIMEZONE_LABEL = getTimezoneLabel();

// Date.getTimezoneOffset() 규약(UTC 동쪽 = 음수). 두 존 모두 DST 없음 — 새 테넌트 존은 여기에 추가.
const FIXED_TIMEZONE_OFFSET_MINUTES = {
  'Asia/Seoul': -9 * 60,
  'Asia/Hong_Kong': -8 * 60,
};

const APP_TIMEZONE_OFFSET_MINUTES = FIXED_TIMEZONE_OFFSET_MINUTES[APP_TIMEZONE] ?? FIXED_TIMEZONE_OFFSET_MINUTES['Asia/Seoul'];

const LANGUAGE_TO_LOCALE = {
  ko: 'ko-KR',
  en: 'en-US',
  ja: 'ja-JP',
  zh: 'zh-CN',
};

const toDate = (value) => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  return new Date(value);
};

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const normalizeLocale = (localeOrLanguage) => {
  if (!localeOrLanguage) {
    return 'en-US';
  }

  return LANGUAGE_TO_LOCALE[localeOrLanguage] || localeOrLanguage;
};

const getDisplayShiftMs = (date) => {
  return (date.getTimezoneOffset() - APP_TIMEZONE_OFFSET_MINUTES) * 60000;
};

const pad = (value) => String(value).padStart(2, '0');

export const getLocaleFromLanguage = (language = 'en') => normalizeLocale(language);

export const toAppTimeZoneDisplayDate = (value) => {
  const date = toDate(value);
  if (!isValidDate(date)) {
    return null;
  }

  return new Date(date.getTime() + getDisplayShiftMs(date));
};

export const fromAppTimeZoneDisplayDate = (value) => {
  const date = toDate(value);
  if (!isValidDate(date)) {
    return null;
  }

  return new Date(date.getTime() - getDisplayShiftMs(date));
};

export const formatInAppTimeZone = (value, localeOrLanguage, options = {}) => {
  const date = toDate(value);
  if (!isValidDate(date)) {
    return '';
  }

  return new Intl.DateTimeFormat(normalizeLocale(localeOrLanguage), {
    timeZone: APP_TIMEZONE,
    ...options,
  }).format(date);
};

export const formatDateInAppTimeZone = (value, localeOrLanguage, options = {}) => {
  return formatInAppTimeZone(value, localeOrLanguage, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  });
};

export const formatTimeInAppTimeZone = (value, localeOrLanguage, options = {}) => {
  return formatInAppTimeZone(value, localeOrLanguage, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options,
  });
};

export const formatDateTimeInAppTimeZone = (value, localeOrLanguage, options = {}) => {
  return formatInAppTimeZone(value, localeOrLanguage, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options,
  });
};

export const toAppTimeZoneInputValue = (value) => {
  const displayDate = toAppTimeZoneDisplayDate(value);
  if (!displayDate) {
    return '';
  }

  return `${displayDate.getFullYear()}-${pad(displayDate.getMonth() + 1)}-${pad(displayDate.getDate())}T${pad(displayDate.getHours())}:${pad(displayDate.getMinutes())}`;
};

export const fromAppTimeZoneInputValue = (value) => {
  if (!value) {
    return '';
  }

  const displayDate = new Date(value);
  if (!isValidDate(displayDate)) {
    return '';
  }

  const utcDate = fromAppTimeZoneDisplayDate(displayDate);
  return utcDate ? utcDate.toISOString() : '';
};
